/**
 * Permission utilities for order editing
 */

import { ORDER_STATUS } from './constants';

/**
 * Check if a user can edit an order based on their type and the order status
 * @param {Object} order - The order object
 * @param {string} userType - The user type ('client', 'prosthetist', 'admin')
 * @returns {boolean} True if the user can edit the order
 */
export const canEditOrder = (order, userType) => {
    if (!order || !userType) return false;

    const status = order.status;

    // No one can edit cancelled or shipped orders (except admin for shipped)
    if (status === ORDER_STATUS.CANCELLED) {
        return false;
    }

    // Admin can edit any non-cancelled order
    if (userType === 'admin') {
        return status !== ORDER_STATUS.CANCELLED;
    }

    // Client can only edit pending orders
    if (userType === 'client') {
        return status === ORDER_STATUS.PENDING;
    }

    // Prosthetist can edit pending and in_progress orders
    if (userType === 'prosthetist') {
        return [ORDER_STATUS.PENDING, ORDER_STATUS.IN_PROGRESS].includes(status);
    }

    return false;
};

/**
 * Get the list of fields that a user type can edit
 * @param {string} userType - The user type ('client', 'prosthetist', 'admin')
 * @returns {string[]|'all'} Array of field names or 'all'
 */
export const getEditableFields = (userType) => {
    if (userType === 'admin' || userType === 'client') {
        return 'all';
    }

    if (userType === 'prosthetist') {
        // Prosthetist cannot edit client info or patient
        return [
            'prosthesisType',
            'prosthesisSubtype',
            'toothNumber',
            'deliveryDate',
            'shade',
            'description'
        ];
    }

    return [];
};

/**
 * Check if a specific field is editable by a user type
 * @param {string} fieldName - The field name in camelCase
 * @param {string} userType - The user type
 * @returns {boolean} True if the field is editable
 */
export const isFieldEditable = (fieldName, userType) => {
    const editableFields = getEditableFields(userType);

    if (editableFields === 'all') {
        return true;
    }

    // For prosthetist, also check if it's a nested client info field
    if (userType === 'prosthetist') {
        // Client info fields (name, clinic, phone, email, address) are NOT editable
        const clientInfoFields = ['name', 'clinic', 'phone', 'email', 'address'];
        if (clientInfoFields.includes(fieldName)) {
            return false;
        }

        // Patient name code is NOT editable
        if (fieldName === 'patientNameCode') {
            return false;
        }
    }

    return editableFields.includes(fieldName);
};

/**
 * Get a human-readable reason why a user cannot edit an order
 * @param {Object} order - The order object
 * @param {string} userType - The user type
 * @returns {string|null} The reason or null if they can edit
 */
export const getEditBlockedReason = (order, userType) => {
    if (!order) return 'Commande non trouvée';
    if (!userType) return 'Utilisateur non connecté';

    const status = order.status;

    if (status === ORDER_STATUS.CANCELLED) {
        return 'Cette commande a été annulée';
    }

    if (status === ORDER_STATUS.SHIPPED && userType !== 'admin') {
        return 'Cette commande a déjà été expédiée';
    }

    if (userType === 'client' && status !== ORDER_STATUS.PENDING) {
        return 'Vous ne pouvez modifier que les commandes en attente';
    }

    if (userType === 'prosthetist' && ![ORDER_STATUS.PENDING, ORDER_STATUS.IN_PROGRESS].includes(status)) {
        return 'Vous ne pouvez modifier que les commandes en attente ou en cours';
    }

    return null; // Can edit
};
